import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { V2Badge } from '@uhg-abyss/web/ui/Badge';
import { config } from '@uhg-abyss/web/tools/config';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Button } from '@src/common/Button';
import { V2Text } from '@uhg-abyss/web/ui/Text';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { useCollapse } from '@uhg-abyss/web/hooks/useCollapse';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';

const StepDescriptions = styled('div', {
  display: 'flex',
  justifyContent: 'space-around',
  marginTop: '10px',
  fontSize: '$sm',
});

const StepDescription = styled('div', {
  textAlign: 'center',
  flex: 1,
  color: '$gray6',
});

const IconContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  marginBottom: '20px',
});

const IconItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
});

const IconLabel = styled('div', {
  marginBottom: '8px',
  fontSize: '$sm',
  fontWeight: 500,
  textAlign: 'center',
  color: '$gray8',
});

// ─── Styled Components ───────────────────────────────────────────────────────
const SubmissionsWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
});

const ColSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '3px',
});

const ColLabel = styled('p', {
  fontSize: '17px',
  fontWeight: 700,
  color: '$core.color.neutral.90',
  margin: 0,
});

const ColValue = styled('p', {
  fontSize: '16px',
  fontWeight: 400,
  color: '$core.color.neutral.90',
  margin: 0,
});

const StepEyebrow = styled('p', {
  display: 'block',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.5px',
  margin: 0,
});

const StepHeading = styled('p', {
  display: 'block',
  fontSize: '20px',
  fontWeight: 700,
  margin: 0,
});

const StepBody = styled('p', {
  display: 'block',
  fontSize: '14px',
  fontWeight: 400,
  color: '$core.color.neutral.90',
  lineHeight: '120%',
  margin: 0,
});

const getCurrentStepStatusConfig = (status) => {
  const map = {
    PENDING: {
      bg: '$core.color.blue.10',
      color: '$core.color.brand.80',
      badgeIcon: 'description',
      badgeLabel: 'Under review',
      badgeVariant: 'info',
    },
    RELEASED: {
      bg: '$core.color.orange.10',
      color: '$core.color.yellow.120',
      badgeIcon: 'warning',
      badgeLabel: 'Action required',
      badgeVariant: 'warning',
    },
    VERIFICATION_PENDING: {
      bg: '$core.color.neutral.20',
      color: '$core.color.neutral.90',
      badgeIcon: 'lock',
      badgeLabel: 'Verification pending',
      badgeVariant: 'neutral',
    },
    ACCEPTED: {
      bg: '$core.color.green.20',
      color: '$core.color.green.100',
      badgeIcon: 'check_circle',
      badgeLabel: 'Approved',
      badgeVariant: 'success',
    },
    DENIED: {
      bg: '$core.color.red.10',
      color: '$core.color.red.120',
      badgeIcon: 'error',
      badgeLabel: 'Denied',
      badgeVariant: 'error',
    },
  };
  return map[status];
};

const formatDate = (dt) => {
  if (!dt) return '-';
  return dt.replace(/-/g, '/');
};

// ─── Single submission card ───────────────────────────────────────────────────
const SubmissionCard = ({ submission }) => {
  const {
    claimsAttribute,
    registrationStatus,
    requestId,
    reviewCompletedOn,
    submittedOn,
    tin,
    trackingNumber,
    updatedResponse,
  } = submission;

  const { collapseProps, buttonProps, isOpen } = useCollapse({
    defaultIsOpen: false,
  });

  const router = useRouter();

  const currentStepStatus =
    registrationStatus === 'RELEASED' && claimsAttribute
      ? 'VERIFICATION_PENDING'
      : registrationStatus;

  const currentStepStatusConfig = getCurrentStepStatusConfig(currentStepStatus);

  const renderCurrentStep = () => {
    if (!currentStepStatusConfig) return null;

    const cardCss = {
      'abyss-card-root': {
        backgroundColor: currentStepStatusConfig.bg,
        border: `1px solid ${currentStepStatusConfig.color}`,
        color: currentStepStatusConfig.color,
        marginTop: '25px',
      },
    };

    switch (currentStepStatus) {
      case 'PENDING':
        return (
          <V2Card css={cardCss}>
            <StepEyebrow>Current step</StepEyebrow>
            <StepHeading>Your submission is under review</StepHeading>
            {updatedResponse ? (
              <StepBody css={{ marginTop: '6px' }}>
                We've received the additional information you've submitted.
                We'll email you when the review is complete.
              </StepBody>
            ) : (
              <>
                <StepBody css={{ marginTop: '6px' }}>
                  We'll email you when the review is complete. If you need to
                  upload another W-9 or add additional servicing addresses, you
                  can update your submission.
                </StepBody>
                <StepBody>
                  <strong>Please note:</strong> you can only do this once.
                </StepBody>
                <div style={{ marginTop: '18px' }}>
                  <Button
                    size="$sm"
                    type="button"
                    variant="outline"
                    onClick={() => {
                      router.navigate('/update-submission', {
                        state: {
                          tin,
                          requestId,
                          trackingNumber,
                          submittedOn,
                        },
                      });
                    }}
                  >
                    Update submission
                  </Button>
                </div>
              </>
            )}
          </V2Card>
        );
      case 'RELEASED':
        return (
          <V2Card css={cardCss}>
            <StepEyebrow>Current step</StepEyebrow>
            <StepHeading>Submit a claim</StepHeading>
            <StepBody css={{ marginTop: '6px' }}>
              To continue, please send a paper claim to the address listed on
              the member's card.
            </StepBody>
          </V2Card>
        );
      case 'VERIFICATION_PENDING':
        return (
          <V2Card css={cardCss}>
            <StepEyebrow>Current step</StepEyebrow>
            <StepHeading>Your TIN verification is pending</StepHeading>
            <StepBody css={{ marginTop: '6px' }}>
              We'll email you when verification is complete.
            </StepBody>
          </V2Card>
        );
      case 'ACCEPTED':
        return (
          <V2Card css={cardCss}>
            <StepEyebrow>Current step</StepEyebrow>
            <StepHeading>
              Congratulations! Your TIN verification was successful
            </StepHeading>
            <StepBody css={{ marginTop: '6px' }}>
              You can now register for the{' '}
              <Link
                href={config('PORTAL_REGISTRATION_URL')}
                target="_blank"
                rel="noreferrer"
                hideIcon
              >
                UnitedHealthcare Provider Portal
              </Link>{' '}
              or{' '}
              <Link
                href={config('API_MARKETPLACE_WELCOME_URL')}
                target="_blank"
                rel="noreferrer"
                hideIcon
              >
                API Marketplace
              </Link>
              .
            </StepBody>
          </V2Card>
        );
      case 'DENIED':
        return (
          <V2Card css={cardCss}>
            <StepEyebrow>Current step</StepEyebrow>
            <StepHeading>This TIN cannot be verified at this time</StepHeading>
            <StepBody css={{ marginTop: '6px' }}>
              You will receive a letter in the mail with more information and
              next steps.
            </StepBody>
          </V2Card>
        );
      default:
        return null;
    }
  };

  const renderTimeline = () => {
    return (
      <V2Card css={{ marginTop: '15px' }}>
        <div style={{ paddingTop: '1em', paddingBottom: '1em' }}>
          <IconContainer>
            <IconItem>
              <IconLabel>Submission</IconLabel>
              <IconSymbol
                icon="check_circle"
                size="32px"
                color="$success1"
                variant="outlined"
              />
            </IconItem>

            <IconItem>
              <IconLabel>Submission review</IconLabel>
              {registrationStatus === 'PENDING' ? (
                <IconSymbol icon="pending" size="32px" color="$info1" />
              ) : (
                <IconSymbol
                  icon="check_circle"
                  size="32px"
                  color="$success1"
                  variant="outlined"
                />
              )}
            </IconItem>

            <IconItem>
              <IconLabel>Claim submission</IconLabel>
              {((registrationStatus === 'PENDING' ||
                registrationStatus === 'RELEASED') &&
                claimsAttribute) ||
              registrationStatus === 'ACCEPTED' ||
              registrationStatus === 'DENIED' ? (
                <IconSymbol
                  icon="check_circle"
                  size="32px"
                  color="$success1"
                  variant="outlined"
                />
              ) : (
                <IconSymbol
                  icon="description"
                  size="32px"
                  color={
                    registrationStatus === 'RELEASED' && !claimsAttribute
                      ? '$warning1'
                      : '$gray6'
                  }
                />
              )}
            </IconItem>

            <IconItem>
              <IconLabel>Verification</IconLabel>
              {registrationStatus === 'ACCEPTED' ? (
                <IconSymbol icon="verified" size="32px" color="$success1" />
              ) : registrationStatus === 'DENIED' ? (
                <IconSymbol icon="cancel" size="32px" color="$error1" />
              ) : (
                <IconSymbol
                  icon="lock"
                  size="32px"
                  variant="filled"
                  color="$gray6"
                />
              )}
            </IconItem>
          </IconContainer>

          <StepDescriptions>
            <StepDescription css={{ color: '$success1' }}>
              Complete
            </StepDescription>

            <StepDescription
              css={{
                color:
                  registrationStatus === 'PENDING' ? '$info1' : '$success1',
              }}
            >
              {registrationStatus === 'PENDING' ? (
                <React.Fragment>
                  In progress
                  <br />
                  <StepDescription css={{ color: '$gray6' }}>
                    You will receive an email once the review is complete.
                  </StepDescription>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  Complete
                  <br />
                  <StepDescription css={{ color: '$gray6' }}>
                    Completed on {reviewCompletedOn || ''}
                  </StepDescription>
                </React.Fragment>
              )}
            </StepDescription>

            <StepDescription
              css={{
                color:
                  ((registrationStatus === 'PENDING' ||
                    registrationStatus === 'RELEASED') &&
                    claimsAttribute) ||
                  registrationStatus === 'ACCEPTED' ||
                  registrationStatus === 'DENIED'
                    ? '$success1'
                    : registrationStatus === 'RELEASED' && !claimsAttribute
                    ? '$warning1'
                    : '$gray6',
              }}
            >
              {((registrationStatus === 'PENDING' ||
                registrationStatus === 'RELEASED') &&
                claimsAttribute) ||
              registrationStatus === 'ACCEPTED' ||
              registrationStatus === 'DENIED'
                ? 'Complete'
                : registrationStatus === 'RELEASED' && !claimsAttribute
                ? 'Required'
                : 'Pending'}
            </StepDescription>

            <StepDescription
              css={{
                color:
                  registrationStatus === 'ACCEPTED'
                    ? '$success1'
                    : registrationStatus === 'DENIED'
                    ? '$error1'
                    : '$gray6',
              }}
            >
              {registrationStatus === 'ACCEPTED' ? (
                <React.Fragment>
                  Approved
                  <br />
                  <StepDescription css={{ color: '$gray6' }}>
                    This TIN has been successfully verified.
                  </StepDescription>
                </React.Fragment>
              ) : registrationStatus === 'DENIED' ? (
                <React.Fragment>
                  Denied
                  <br />
                  <StepDescription css={{ color: '$gray6' }}>
                    This TIN cannot be validated at this time.
                  </StepDescription>
                </React.Fragment>
              ) : (
                <React.Fragment>Pending</React.Fragment>
              )}
            </StepDescription>
          </StepDescriptions>
        </div>
      </V2Card>
    );
  };

  return (
    <V2Card>
      <Layout.Group
        css={{ justifyContent: 'space-between', cursor: 'pointer' }}
        {...buttonProps}
      >
        <Layout.Group>
          <ColSection css={{ width: '340px' }}>
            <Layout.Group css={{ gap: '5px', alignItems: 'center' }}>
              <V2Text
                css={{ fontSize: '23px', fontWeight: 700, color: '$primary1' }}
              >
                {tin}
              </V2Text>
              <V2Badge
                variant={currentStepStatusConfig?.badgeVariant}
                icon={currentStepStatusConfig?.badgeIcon}
                outline
              >
                {currentStepStatusConfig?.badgeLabel}
              </V2Badge>
            </Layout.Group>
          </ColSection>

          <ColSection css={{ width: '140px' }}>
            <ColLabel>Submitted</ColLabel>
            <ColValue>{formatDate(submittedOn)}</ColValue>
          </ColSection>

          <ColSection>
            <ColLabel>Reference number</ColLabel>
            <ColValue>{trackingNumber || '—'}</ColValue>
          </ColSection>
        </Layout.Group>

        <IconSymbol
          icon={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          size={40}
          color="$primary1"
          css={{ cursor: 'pointer' }}
        />
      </Layout.Group>

      <div {...collapseProps}>
        {renderCurrentStep()}
        {renderTimeline()}
      </div>
    </V2Card>
  );
};

// ─── Main export ─────────────────────────────────────────────────────────────
export const SubmittedRegistrations = ({ submittedList }) => {
  return submittedList && submittedList.length > 0 ? (
    <SubmissionsWrapper id="submitted-registration">
      {submittedList.map((submission, index) => (
        <SubmissionCard key={index} submission={submission} />
      ))}
    </SubmissionsWrapper>
  ) : (
    <V2Text
      css={{
        display: 'block',
        color: '$primary1',
        fontSize: '20px',
        fontWeight: 700,
        textAlign: 'center',
      }}
      id="no-submissions-message"
    >
      No submissions yet
    </V2Text>
  );
};
